import { normalizeEmailThread } from "./adminEmailThreadNormalize";

/**
 * Short label for the service-requests table badge (client email offer stance).
 * @param {string} signal
 */
export function shortLabelForOfferSignal(signal) {
  const s = String(signal || "").trim();
  if (s === "accept_offer") return "Accept";
  if (s === "reject_offer") return "Decline";
  if (s === "negotiate") return "Negotiate";
  return "Unclear";
}

/**
 * @param {{ confidence?: number } | null | undefined} ca
 */
export function formatConfidenceSuffix(ca) {
  const c = ca?.confidence;
  if (c == null || typeof c !== "number" || Number.isNaN(c)) return "";
  const pct = c <= 1 ? Math.round(c * 100) : Math.round(c);
  return ` · ${pct}%`;
}

/**
 * @param {Record<string, unknown>} row
 */
function formatConfidenceFromDenormRow(row) {
  const c = row.clientEmailOfferConfidence ?? row.lastInboundOfferConfidence;
  if (c == null || typeof c !== "number" || Number.isNaN(c)) return "";
  const pct = c <= 1 ? Math.round(c * 100) : Math.round(c);
  return ` · ${pct}%`;
}

/**
 * Service request **list** row: offer line = AI on the **latest inbound** message (or API overrides).
 *
 * **Optional list-only API fields:** `clientReplyListLine` (preformatted), `clientEmailOfferSignal` + label/confidence, etc.
 *
 * @param {Record<string, unknown> | null | undefined} row
 * @returns {{ kind: 'none' } | { kind: 'pending', hint?: string } | { kind: 'failed', hint?: string } | { kind: 'offer', signal: string, label: string, detail?: string }}
 */
export function getServiceRequestListClientEmailOffer(row) {
  if (!row || typeof row !== "object") return { kind: "none" };

  const replyLine = row.clientReplyListLine ?? row.clientEmailReplyListLine;
  if (replyLine != null && String(replyLine).trim() !== "") {
    const sig =
      row.clientEmailOfferSignal ??
      row.lastInboundOfferSignal ??
      row.emailOfferSignal ??
      "unclear";
    return {
      kind: "offer",
      signal: String(sig).trim() || "unclear",
      label: String(replyLine).trim(),
      detail: row.clientEmailOfferLabel
        ? String(row.clientEmailOfferLabel)
        : undefined,
    };
  }

  const denormSignal =
    row.clientEmailOfferSignal ?? row.lastInboundOfferSignal ?? row.emailOfferSignal;
  if (denormSignal != null && String(denormSignal).trim() !== "") {
    const signal = String(denormSignal).trim();
    const rawLab =
      row.clientEmailOfferLabel ?? row.lastInboundOfferLabel ?? row.emailOfferLabel;
    const custom = rawLab != null ? String(rawLab).trim() : "";
    const base = custom || shortLabelForOfferSignal(signal);
    return {
      kind: "offer",
      signal,
      label: base + formatConfidenceFromDenormRow(row),
      detail: custom || undefined,
    };
  }

  const raw = row.emailMessages ?? row.emailThread;
  if (!Array.isArray(raw) || raw.length === 0) {
    return { kind: "none" };
  }

  const thread = normalizeEmailThread(raw, row.email ?? "");
  const inbounds = thread.filter((m) => m.direction === "inbound");
  if (inbounds.length === 0) return { kind: "none" };

  inbounds.sort(
    (a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime()
  );
  const latest = inbounds[0];
  const ca = latest?.clientAnalysis;
  if (!ca || typeof ca !== "object") return { kind: "none" };

  const st = String(ca.status || "").toLowerCase();
  if (st === "pending") {
    return {
      kind: "pending",
      hint: "Inbound email analysis in progress — latest client message",
    };
  }
  if (st === "failed") {
    return {
      kind: "failed",
      hint: typeof ca.errorMessage === "string" ? ca.errorMessage : "Analysis failed",
    };
  }
  if (st === "completed") {
    const signal = String(ca.offerSignal || "unclear").trim() || "unclear";
    const custom = (ca.offerLabel || "").trim();
    const base = custom || shortLabelForOfferSignal(signal);
    return {
      kind: "offer",
      signal,
      label: base + formatConfidenceSuffix(ca),
      detail: (ca.summary || "").trim() || custom || undefined,
    };
  }

  return { kind: "none" };
}
